import * as React from 'react';
import { JSSClassesProps } from 'react-jss';

import { PhotoGallery } from '../PhotoGallery';
import { Map } from '../HotelOnMap/Map';

import { ComponentJSSClasses } from './styles';
import { getHotelPhotoUrl, getPlaceHolderImage } from '../../../utils/url';
import { UiImageWithLoader, UiImageWithLoaderPendingMode } from 'sletat-uikit2/dist/js/UiImageWithLoader';
import { NoPhotoPlaceholder } from '../PhotoGallery/NoPhotoPlaceholder';
import { FullScreenCarousel } from '../FullScreenCarousel';
import { PhotoGalleryPlaceholder } from '../HotelPagePlaceholder/PhotoGalleryPlaceholder';
import { Portal } from '../../../../components/common/Portal';

const SMALL_PHOTO_HEIGHT = 110;
const SMALL_PHOTO_MAX_WIDTH = 202;

const MIDDLE_PHOTO_HEIGHT = 227;
const MIDDLE_PHOTO_MAX_WIDTH = 411;

const LARGE_PHOTO_HEIGHT = 465;
const LARGE_PHOTO_MAX_WIDTH = 1024;
const LARGE_PHOTO_MAX_HEIGHT = 576;

export interface HotelPhotoGalleryProps {
    isMobileView: boolean;
    hotelId: number | null;
    resortId: number | null;
    hotelName: string | null;
    hotelcategoryName: string | null;
    hotelImagesCount: number | null;
    hotelBeachSandType: string | null;
    hotelLatitude: number | null;
    hotelLongitude: number | null;
}

interface HotelPhotoGalleryState {
    isCarouselOpen: boolean;
    carouselInitialIndex: number;
    isClient: boolean;
}

export interface StyledHotelPhotoGalleryProps extends JSSClassesProps<ComponentJSSClasses>, HotelPhotoGalleryProps { }

export class HotelPhotoGallery extends React.Component<StyledHotelPhotoGalleryProps, HotelPhotoGalleryState> {

    constructor(props: StyledHotelPhotoGalleryProps) {
        super(props);
        this.state = {
            isCarouselOpen: false,
            carouselInitialIndex: 0,
            isClient: false,
        };
    }

    componentDidMount() {
        this.setState({isClient: true});
    }

    render() {
        const { classes } = this.props;

        if (!this.state.isClient) {
            return <PhotoGalleryPlaceholder />;
        }

        if (this.props.isMobileView ||
            (this.isHotelHasOnlyOnePhoto && this.isHotelCoordinatesNotDefined)) {
                return (
                    <PhotoGallery
                        isMobileView={this.props.isMobileView}
                        hotelId={this.props.hotelId}
                        resortId={this.props.resortId}
                        hotelName={this.props.hotelName}
                        hotelImagesCount={this.props.hotelImagesCount}
                        hotelBeachSandType={this.props.hotelBeachSandType}
                    />
                );
            }

        return (
            <div className={classes.main}>
                {this.state.isCarouselOpen && !!this.props.hotelImagesCount && (
                    <Portal>
                        <FullScreenCarousel
                            initialSlide={this.state.carouselInitialIndex}
                            imagesCount={this.props.hotelImagesCount}
                            title={(<>{this.props.hotelcategoryName} <span className={classes.star}>★</span> {this.props.hotelName}</>)}
                            getImageUrl={this.getImageUrl}
                            onClose={this.onCarouselClose}
                        />
                    </Portal>
                )}
                <div className={classes.left}>
                    {this.renderLeftBlock()}
                </div>
                <div className={classes.right}>
                    {this.renderRightBlock()}
                </div>
            </div>
        );
    }

    renderLeftBlock(): JSX.Element | null {
        const { classes } = this.props;

        return (
            <>
                {this.props.hotelBeachSandType &&
                    this.renderHotelLabels()
                }
                <div
                    className={classes.largePhotoWrap}
                    onClick={() => this.openCarousel()}
                >
                    {this.renderLeftPhoto()}
                </div>
            </>
        );
    }

    renderHotelLabels(): JSX.Element | null {
        const { classes } = this.props;

        return (
            <div className={classes.hotelLabels}>
                {this.props.hotelBeachSandType}
            </div>
        );
    }

    renderLeftPhoto(): JSX.Element | null {
        if (!this.props.hotelId || !this.props.hotelImagesCount) {
            return this.renderPlaceholderPhoto();
        }

        return this.renderHotelPhoto(0, LARGE_PHOTO_HEIGHT, LARGE_PHOTO_MAX_WIDTH);
    }

    renderPlaceholderPhoto(): JSX.Element | null {
        if (!this.props.resortId) {
            return null;
        }

        const src = getPlaceHolderImage(LARGE_PHOTO_MAX_WIDTH, LARGE_PHOTO_MAX_HEIGHT);

        return (
            <UiImageWithLoader
                src={src}
                errorStatus={<NoPhotoPlaceholder />}
                pendingMode={UiImageWithLoaderPendingMode.once}
                loaderStyles={{
                    width: `100%`,
                    height: `${LARGE_PHOTO_HEIGHT}px`
                }}
            />
        );
    }

    renderRightBlock(): JSX.Element | null {
        if (!this.props.hotelImagesCount || this.props.hotelImagesCount === 1) {
            return (
                <Map
                    latitude={this.props.hotelLatitude!}
                    longitude={this.props.hotelLongitude!}
                    isAdaptive={true}
                />
            );
        }

        const { classes } = this.props;

        return (
            <>
                <div className={`${classes.rightItem} ${classes.zoomable}`} onClick={() => this.openCarousel(1)}>
                    {this.renderHotelPhoto(1, MIDDLE_PHOTO_HEIGHT, MIDDLE_PHOTO_MAX_WIDTH)}
                </div>
                <div className={classes.rightItem}>
                    {this.renderThirdBlock()}
                </div>
            </>
        );
    }

    renderThirdBlock(): JSX.Element | null {
        if (!this.props.hotelImagesCount) {
            return null;
        }

        const { classes } = this.props;

        if (this.props.hotelImagesCount === 2) {
            return (
                <Map
                    latitude={this.props.hotelLatitude!}
                    longitude={this.props.hotelLongitude!}
                    isAdaptive={true}
                />
            );
        }

        if (this.props.hotelImagesCount === 3) {
            return (
                <div className={classes.zoomable} onClick={() => this.openCarousel(2)}>
                    {this.renderHotelPhoto(2, MIDDLE_PHOTO_HEIGHT, MIDDLE_PHOTO_MAX_WIDTH)}
                </div>
            );
        }

        if (this.props.hotelImagesCount > 3 && this.props.hotelImagesCount <= 6) {
            return (
                <>
                    {this.renderHotelPhoto(2, MIDDLE_PHOTO_HEIGHT, MIDDLE_PHOTO_MAX_WIDTH)}
                    <div className={classes.blackout} onClick={() => this.openCarousel()}>
                        <span className={classes.morePhotos}>+{this.props.hotelImagesCount - 2} фотографии</span>
                    </div>
                </>
            );
        }

        return (
            <>
                <div className={`${classes.smallPhoto} ${classes.zoomable}`} onClick={() => this.openCarousel(2)}>
                    {this.renderHotelPhoto(2, SMALL_PHOTO_HEIGHT, SMALL_PHOTO_MAX_WIDTH)}
                </div>
                <div className={`${classes.smallPhoto} ${classes.zoomable}`} onClick={() => this.openCarousel(3)}>
                    {this.renderHotelPhoto(3, SMALL_PHOTO_HEIGHT, SMALL_PHOTO_MAX_WIDTH)}
                </div>
                <div className={`${classes.smallPhoto} ${classes.zoomable}`} onClick={() => this.openCarousel(4)}>
                    {this.renderHotelPhoto(4, SMALL_PHOTO_HEIGHT, SMALL_PHOTO_MAX_WIDTH)}
                </div>
                <div className={classes.smallPhoto} onClick={() => this.openCarousel()}>
                    {this.renderHotelPhoto(5, SMALL_PHOTO_HEIGHT, SMALL_PHOTO_MAX_WIDTH)}
                    <div className={classes.blackout}>
                        <span className={classes.morePhotos}>+{this.props.hotelImagesCount - 5} фотографии</span>
                    </div>
                </div>
            </>
        );
    }

    renderHotelPhoto(index: number, height: number, maxWidth: number): JSX.Element | null {
        if (!this.props.hotelId || !this.props.hotelImagesCount) {
            return null;
        }

        const src = getHotelPhotoUrl({
            hotelId: this.props.hotelId,
            index: index,
            width: maxWidth,
            height: Math.round(maxWidth / 1.8)
        });

        return (
            <UiImageWithLoader
                src={src}
                pendingMode={UiImageWithLoaderPendingMode.once}
                imgProps={{ alt: this.props.hotelName || '' }}
                loaderStyles={{
                    width: `100%`,
                    height: `${height}px`
                }}
            />
        );
    }

    getImageUrl = (index: number, width: number, height: number) => {
        if (!this.props.hotelId) {
            return '';
        }

        return getHotelPhotoUrl({
            hotelId: this.props.hotelId,
            index: index,
            width: width,
            height: height
        });
    }

    openCarousel = (initialIndex?: number) => {
        this.setState({
            isCarouselOpen: true,
            carouselInitialIndex: initialIndex || 0
        });
    }

    onCarouselClose = () => {
        this.setState({
            isCarouselOpen: false,
            carouselInitialIndex: 0
        });
    }

    private get isHotelCoordinatesNotDefined(): boolean {
        return !this.props.hotelLatitude && !this.props.hotelLongitude;
    }

    private get isHotelHasOnlyOnePhoto(): boolean {
        return (!this.props.hotelImagesCount || this.props.hotelImagesCount === 1);
    }
}
